import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { adminLogin } from '../../lib/adminApi';

export default function AdminLogin() {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      await adminLogin(password);
      navigate('/admin/enquiries', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Try again.';
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen grid place-items-center bg-brand-cream px-4">
      <div className="w-full max-w-sm bg-white rounded-3xl border border-brand-ink/10 p-7 md:p-9">
        <div className="mx-auto w-12 h-12 rounded-2xl bg-brand-sun grid place-items-center">
          <Lock className="w-6 h-6 text-brand-ink" aria-hidden="true" />
        </div>
        <h1 className="mt-5 font-display text-2xl md:text-3xl text-brand-ink text-center">
          Science Sorted CMS
        </h1>
        <p className="mt-1 text-sm text-brand-ink/70 text-center">
          Sign in to edit site content.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2"
              data-testid="admin-login-password"
              autoFocus
            />
          </div>
          {error && (
            <div
              role="alert"
              data-testid="admin-login-error"
              className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting || !password}
            data-testid="admin-login-submit"
            className="w-full bg-brand-ink text-brand-cream hover:bg-brand-teal rounded-full h-11 gap-2 disabled:opacity-70"
          >
            {submitting ? (
              <>Signing in <Loader2 className="w-4 h-4 animate-spin" /></>
            ) : (
              <>Sign in</>
            )}
          </Button>
        </form>

        <a
          href="/"
          className="mt-5 inline-flex items-center gap-1.5 text-xs text-brand-ink/60 hover:text-brand-ink justify-center w-full"
        >
          View website <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </main>
  );
}
